/** @jsxImportSource @emotion/react */
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import qs from 'query-string';
import Paper from '@mui/material/Paper';
import MuiTable from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';

import {
  tableContainerStyles,
  paperStyles,
  scrollContainerStyles,
  headerCellStyles,
  bodyCellStyles,
  linkStyles,
} from './Table.styles';

// Same MUI markup and cell styling as Table2, keeping this component's config-driven API.
const Table = ({
  history,
  containerStyles,
  tableHeaderContainerCustomStyles,
  styles,
  tableConfig,
  showPagination,
  pagination,
  setPagination,
  data,
  dataContainerStyles,
  isHoverable,
  onRowClick,
}) => {
  const { current = 1, total = 0, totalData } = pagination || {};

  const handlePageChange = (e, newPage) => {
    const page = newPage + 1;
    setPagination((prevState) => ({
      ...prevState,
      current: page,
    }));
    history.push({
      pathname: history.location.pathname,
      search: qs.stringify({ ...qs.parse(history.location.search), page }),
    });
  };

  const rowHref = (d) => `/${d.id}/${d.subpage ? d.subpage : 'personal-data'}${d.program ? `?program=${d.program || 'ftp'}` : ''}`;

  return (
    <div css={[tableContainerStyles, containerStyles]}>
      <Paper css={paperStyles}>
        <TableContainer css={scrollContainerStyles}>
          <MuiTable stickyHeader css={[styles, tableHeaderContainerCustomStyles]}>
            <TableHead>
              <TableRow>
                {tableConfig.map(({
                  key, label, ratio, textAlign,
                }) => (
                  <TableCell
                    key={key}
                    align={textAlign}
                    css={headerCellStyles}
                    style={{ width: `${100 * ratio}%` }}
                  >
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((d, index) => (
                <TableRow
                  hover
                  tabIndex={-1}
                  key={d.key || d.id || index}
                  css={dataContainerStyles}
                  style={onRowClick ? { cursor: 'pointer' } : undefined}
                  onClick={onRowClick ? (e) => onRowClick(d, e) : undefined}
                >
                  {tableConfig.map(({ key, textAlign, component }) => (
                    <TableCell key={key} align={textAlign} css={bodyCellStyles}>
                      {
                        isHoverable && key !== 'action' && key !== 'name' && key !== 'id' ? (
                          <a href={rowHref(d)} css={linkStyles}>
                            {component(d[key])}
                          </a>
                        ) : component(d[key])
                      }
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </MuiTable>
        </TableContainer>
        {
          showPagination && (
            <TablePagination
              component="div"
              count={total}
              rowsPerPage={1}
              rowsPerPageOptions={[]}
              page={total ? Math.min(Math.max(current - 1, 0), total - 1) : 0}
              onPageChange={handlePageChange}
              labelDisplayedRows={() => `Page ${total ? current : 0} of ${total}${totalData !== undefined ? ` · ${totalData} rows` : ''}`}
            />
          )
        }
      </Paper>
    </div>
  );
};

Table.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
    location: PropTypes.shape({
      pathname: PropTypes.string,
      search: PropTypes.string,
    }),
  }),
  containerStyles: PropTypes.oneOfType([PropTypes.shape({}), PropTypes.string]),
  tableHeaderContainerCustomStyles: PropTypes.shape({}),
  dataContainerStyles: PropTypes.shape({}),
  styles: PropTypes.shape({}),
  // Kept for existing callers; column widths now come from the table itself.
  tableBodyStyles: PropTypes.shape({}),
  tableConfig: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string,
      label: PropTypes.string,
      ratio: PropTypes.number,
      textAlign: PropTypes.string,
    }),
  ).isRequired,
  showPagination: PropTypes.bool,
  pagination: PropTypes.shape({
    current: PropTypes.number,
    total: PropTypes.number,
    totalData: PropTypes.number,
  }),
  setPagination: PropTypes.func,
  data: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isHoverable: PropTypes.bool,
  onRowClick: PropTypes.func,
};

Table.defaultProps = {
  history: { push: () => {}, location: { pathname: '', search: '' } },
  showPagination: true,
  tableHeaderContainerCustomStyles: css``,
  containerStyles: css``,
  dataContainerStyles: css``,
  styles: css``,
  tableBodyStyles: css``,
  isHoverable: true,
  onRowClick: null,
  pagination: {},
  setPagination: () => {},
};

export default Table;
